import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  getCurrentUser,
  signOut as amplifySignOut,
  fetchAuthSession,
  type AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  isAdmin: boolean;
  email: string;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [email, setEmail] = useState('');

  const checkUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (token) {
          const payload = decodeJwtPayload(token);
          const groups = payload['cognito:groups'];
          setIsAdmin(Array.isArray(groups) ? groups.includes('admin') : groups === 'admin');
          if (typeof payload['email'] === 'string') setEmail(payload['email']);
        }
      } catch {
        setIsAdmin(false);
      }
    } catch {
      setUser(null);
      setIsAdmin(false);
      setEmail('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void checkUser();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') void checkUser();
      if (payload.event === 'signedOut') setUser(null);
    });

    return unsubscribe;
  }, [checkUser]);

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
  }, []);

  const getAccessToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('No valid session');
    return token;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, email, signOut, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
